import type { ProductOwnerBacklogDay } from '../stores/product_owner_backlog'

// Pure line-chart geometry for the Activity tab's backlog burndown — inline
// SVG, no chart library, kept apart from the rendering component so the math
// is unit-testable. Every series is laid out against one shared frame: the x
// axis spans the whole selected window (so a short history reads as "starts
// here", not as a squashed line) and the y axis starts at zero (so the
// backlog's size is honest, not exaggerated by a tight min/max). Consecutive
// days join into one polyline; a missing day or null count breaks the line.
export type BacklogSeries = 'human' | 'total'

export interface BacklogChartPoint {
  date: string
  value: number
  x: number
  y: number
}

export interface BacklogFrame {
  start: string
  end: string
  spanDays: number
  yMax: number
  yTicks: number[]
}

export interface BacklogSeriesGeometry {
  segments: BacklogChartPoint[][]
  points: BacklogChartPoint[]
  latest: number | null
  // Last value minus first value over the plotted points — null with fewer
  // than two points, since one observation is not a trend.
  delta: number | null
}

export const VIEW_WIDTH = 640
export const VIEW_HEIGHT = 180
export const PLOT = { left: 28, right: VIEW_WIDTH - 8, top: 8, bottom: VIEW_HEIGHT - 20 }
// Per-day markers turn into a bumpy chain past about a month of points.
export const MAX_MARKED_POINTS = 31
// Narrower than this and the "no snapshots before" note won't fit its span.
const MIN_EMPTY_SPAN_FOR_NOTE = 140
const MAX_Y_TICK_STEPS = 4
const NICE_STEPS = [1, 2, 5]
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MS_PER_DAY = 86_400_000

function valueOf(day: ProductOwnerBacklogDay, series: BacklogSeries): number | null {
  return series === 'human' ? day.human_open_issue_count : day.open_issue_count
}

function dayOffset(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / MS_PER_DAY)
}

function shiftDate(date: string, days: number): string {
  return new Date(Date.parse(date) + days * MS_PER_DAY).toISOString().slice(0, 10)
}

// Smallest whole 1/2/5×10^n step that covers `max` in at most MAX_Y_TICK_STEPS.
export function niceStep(max: number): number {
  const raw = max / MAX_Y_TICK_STEPS
  if (!Number.isFinite(raw) || raw <= 1) return 1
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  return NICE_STEPS.map((s) => s * magnitude).find((s) => s >= raw) ?? 10 * magnitude
}

export function buildBacklogFrame(days: ProductOwnerBacklogDay[], windowDays: number): BacklogFrame | null {
  if (days.length === 0) return null
  const end = days[days.length - 1].date
  const max = Math.max(...days.flatMap((d) => [d.open_issue_count, d.human_open_issue_count ?? 0]))
  const step = niceStep(Math.max(max, 1))
  const yMax = Math.ceil(Math.max(max, 1) / step) * step
  const yTicks = Array.from({ length: yMax / step + 1 }, (_, i) => i * step)
  // The window can reach one day past the latest snapshot's own window when
  // today's capture hasn't run yet, so never start after the first point.
  const windowStart = shiftDate(end, -windowDays)
  const start = days[0].date < windowStart ? days[0].date : windowStart
  return { start, end, spanDays: Math.max(dayOffset(start, end), 1), yMax, yTicks }
}

export function xFor(frame: BacklogFrame, date: string): number {
  return PLOT.left + (dayOffset(frame.start, date) / frame.spanDays) * (PLOT.right - PLOT.left)
}

export function yFor(frame: BacklogFrame, value: number): number {
  return PLOT.bottom - (value / frame.yMax) * (PLOT.bottom - PLOT.top)
}

export function buildBacklogSeries(
  days: ProductOwnerBacklogDay[],
  series: BacklogSeries,
  frame: BacklogFrame,
): BacklogSeriesGeometry {
  const points = days.flatMap((day) => {
    const value = valueOf(day, series)
    return value == null ? [] : [{ date: day.date, value, x: xFor(frame, day.date), y: yFor(frame, value) }]
  })
  const segments: BacklogChartPoint[][] = []
  points.forEach((point, i) => {
    const previous = points[i - 1]
    if (previous && dayOffset(previous.date, point.date) === 1) segments[segments.length - 1].push(point)
    else segments.push([point])
  })
  const last = points[points.length - 1]
  return {
    segments,
    points,
    latest: last?.value ?? null,
    delta: points.length < 2 ? null : last.value - points[0].value,
  }
}

export function polylinePoints(segment: BacklogChartPoint[]): string {
  return segment.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
}

// Closed path under a segment down to the zero baseline, for the area fill.
export function areaPath(segment: BacklogChartPoint[]): string {
  if (segment.length === 0) return ''
  const first = segment[0]
  const last = segment[segment.length - 1]
  const line = segment.map((p) => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
  return `M${first.x.toFixed(1)},${PLOT.bottom} ${line} L${last.x.toFixed(1)},${PLOT.bottom} Z`
}

export interface BacklogAxisLabel {
  date: string
  x: number
  text: string
  anchor: 'start' | 'middle' | 'end'
}

// Windows up to this long keep the three-label start/middle/end axis.
const SHORT_WINDOW_DAYS = 45
const TICK_INTERVAL_DAYS = 30
const PEAK_LABEL_EDGE_MARGIN = 40
const PEAK_LABEL_RISE = 6
const PEAK_LABEL_MIN_Y = 12
const PEAK_LABEL_DROP = 14

export function buildXAxisLabels(frame: BacklogFrame): BacklogAxisLabel[] {
  const label = (date: string, anchor: BacklogAxisLabel['anchor']) => ({
    date,
    x: xFor(frame, date),
    text: formatShortDate(date),
    anchor,
  })
  if (frame.spanDays <= SHORT_WINDOW_DAYS) {
    const mid = shiftDate(frame.start, Math.round(frame.spanDays / 2))
    return [label(frame.start, 'start'), label(mid, 'middle'), label(frame.end, 'end')]
  }
  // Long windows tick back from the end every ~30 days so each label sits on
  // a stretch of the axis that can hold data.
  const ticks = []
  for (let offset = TICK_INTERVAL_DAYS; offset < frame.spanDays; offset += TICK_INTERVAL_DAYS) {
    ticks.unshift(label(shiftDate(frame.end, -offset), 'middle'))
  }
  return [...ticks, label(frame.end, 'end')]
}

export interface BacklogPeakLabel {
  x: number
  y: number
  text: string
  anchor: BacklogAxisLabel['anchor']
}

// Marks the window's highest point (the latest one on a tie) so a spike is
// readable without hovering — null with fewer than two points, where "peak"
// says nothing the headline number doesn't.
export function peakLabel(points: BacklogChartPoint[]): BacklogPeakLabel | null {
  if (points.length < 2) return null
  const peak = points.reduce((best, p) => (p.value >= best.value ? p : best))
  const above = peak.y - PEAK_LABEL_RISE
  return {
    x: peak.x,
    y: above < PEAK_LABEL_MIN_Y ? peak.y + PEAK_LABEL_DROP : above,
    text: `${peak.value} · ${formatShortDate(peak.date)}`,
    anchor:
      peak.x > PLOT.right - PEAK_LABEL_EDGE_MARGIN
        ? 'end'
        : peak.x < PLOT.left + PEAK_LABEL_EDGE_MARGIN
          ? 'start'
          : 'middle',
  }
}

// Where to explain a blank stretch at the left of the window, so it doesn't
// read as a rendering bug — null when the history nearly fills the window.
export function emptySpanNote(frame: BacklogFrame, firstDate: string): { x: number; text: string } | null {
  const firstX = xFor(frame, firstDate)
  if (firstX - PLOT.left < MIN_EMPTY_SPAN_FOR_NOTE) return null
  return { x: (PLOT.left + firstX) / 2, text: `No snapshots before ${formatShortDate(firstDate)}` }
}

// The point whose x is closest to a pointer position in viewBox units — null
// when there are no points. Ties go to the earlier point.
export function nearestPoint(points: BacklogChartPoint[], x: number): BacklogChartPoint | null {
  return points.reduce<BacklogChartPoint | null>(
    (best, p) => (best === null || Math.abs(p.x - x) < Math.abs(best.x - x) ? p : best),
    null,
  )
}

// Maps a pointer's clientX onto the viewBox x axis for an svg of the given
// on-screen box; null when the box has no width (not laid out yet).
export function clientXToViewX(clientX: number, box: { left: number; width: number }): number | null {
  if (box.width <= 0) return null
  return ((clientX - box.left) / box.width) * VIEW_WIDTH
}

export function pointTitle(point: BacklogChartPoint, humanCount: number | null): string {
  const human = humanCount == null ? '' : ` (${humanCount} human-filed)`
  return `${formatShortDate(point.date)}: ${point.value} open${human}`
}

// A fixed table rather than toLocaleDateString, whose short month varies by
// ICU version ("Sep" vs "Sept").
export function formatShortDate(date: string): string {
  const [, month, day] = date.split('-').map(Number)
  return `${day} ${MONTHS[month - 1]}`
}

// Emerald when the backlog shrank, red when it grew — the delta is also
// spelled out as text so the direction never depends on colour alone.
export function deltaColorClass(delta: number | null): string {
  if (delta == null || delta === 0) return 'text-white/60'
  return delta < 0 ? 'text-emerald-300' : 'text-red-300'
}

// Measured from the first plotted day, not the window start — the history
// can be shorter than the selected window.
export function formatDelta(delta: number | null, since: string | null): string {
  if (delta == null || since == null) return 'Not enough data for a trend yet'
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '±'
  return `${sign}${Math.abs(delta)} since ${formatShortDate(since)}`
}

// First day carrying a human-filed count — how far back the overlay reaches.
export function humanOverlaySince(days: ProductOwnerBacklogDay[]): string | null {
  return days.find((d) => d.human_open_issue_count != null)?.date ?? null
}
